import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { useAccount } from './useAccount'
import type { User, Job, Customer, UserUpsertResult, Licence } from '../types/license'
import type { UserRoleUpdate, Team, CustomerMember, CustomerPerson, FinanceViewer, CustomerEmailDomainsResponse } from '../types/gotypes'
import { PLATINUM_ROLE, RUBY_ROLE, ADMIN_ROLE, API_BASE_URL } from '../constants/system'
import { extractErrorMessage } from '../utils/apitools'

export interface UseAdminReturn {
  // Users
  users: User[]
  usersLoading: boolean
  usersError: string | null
  refetchUsers: () => void

  // Jobs
  jobs: Job[]
  jobsLoading: boolean
  jobsError: string | null
  refetchJobs: () => Promise<{ data?: Job[] }>

  // License Limits
  platinumLimit: number
  rubyLimit: number
  licensesLoading: boolean
  licensesError: string | null

  // User Mutations
  createUser: (user: Partial<User>) => Promise<UserUpsertResult>
  updateUser: (userId: string, user: Partial<User>) => Promise<UserUpsertResult>
  /**
   * Take a person off THIS customer only (their other customers, jobs and
   * realms stay). The account itself goes only when this was their last
   * customer and they are not a Manager: accountDeleted says which.
   */
  removeUserFromCustomer: (userId: string) => Promise<{ accountDeleted: boolean }>
  resetPassword: (userId: string, password: string) => Promise<UserUpsertResult>
  updateUsersRoles: (updates: UserRoleUpdate[]) => Promise<void>
  /** Replace a user's whole roles string, as DNA did. Manager only. */
  setUserRolesString: (userId: string, roles: string) => Promise<void>

  // Job Mutations
  createJob: (job: Partial<Job>) => Promise<Job>
  updateJob: (jobId: string, job: Partial<Job>) => Promise<Job>
  deleteJob: (jobId: string) => Promise<void>

  // Mutation states
  isCreating: boolean
  isUpdating: boolean
  isDeleting: boolean
  isUpdatingRoles: boolean
  isCreatingJob: boolean
  isUpdatingJob: boolean
  isDeletingJob: boolean
  mutationError: string | null
}

/**
 * Calculate the total quota for active licenses of a given role.
 * A license is considered active if the current date is between its startDate and endDate.
 * If no dates are set, the license is considered active.
 */
const calculateActiveLicenses = (licences: Licence[] | null | undefined, roleName: string): number => {
  if (!licences) return 0

  const now = new Date()

  return licences
    .filter(l => l.name?.toLowerCase() === roleName.toLowerCase())
    .filter(l => {
      // If no dates are set, include the license
      if (!l.startDate || !l.endDate) return true

      const start = new Date(l.startDate)
      const end = new Date(l.endDate)
      return now >= start && now <= end
    })
    .reduce((sum, l) => sum + (l.quota || 0), 0)
}

export const useAdmin = (customerId: string | undefined): UseAdminReturn => {
  const account = useAccount()
  const queryClient = useQueryClient()
  const sessionId = account.user?.session_info?.sessionId || ''

  // Users Query
  const usersQuery = useQuery<User[], Error>({
    queryKey: ['admin', 'customers', customerId, 'users'],
    queryFn: async () => {
      if (!customerId || !sessionId) {
        throw new Error('Missing customer ID or session ID')
      }

      const response = await axios.get(
        `/api/v1/admin/customers/${customerId}/users`,
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'x-session-id': sessionId,
          },
        }
      )

      return response.data
    },
    enabled: !!customerId && !!sessionId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })

  // Jobs Query
  const jobsQuery = useQuery<Job[], Error>({
    queryKey: ['admin', 'customers', customerId, 'jobs'],
    queryFn: async () => {
      if (!customerId || !sessionId) {
        throw new Error('Missing customer ID or session ID')
      }

      const response = await axios.get(
        `/api/v1/admin/customers/${customerId}/jobs`,
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'x-session-id': sessionId,
          },
        }
      )

      return response.data
    },
    enabled: !!customerId && !!sessionId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })

  // Licenses Query
  const licensesQuery = useQuery<Customer, Error>({
    queryKey: ['admin', 'customers', customerId, 'licenses'],
    queryFn: async () => {
      if (!customerId || !sessionId) {
        throw new Error('Missing customer ID or session ID')
      }

      const response = await axios.get(
        `/api/v1/admin/customers/${customerId}/licenses`,
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'x-session-id': sessionId,
          },
        }
      )

      return response.data
    },
    enabled: !!customerId && !!sessionId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })

  // Create User Mutation
  const createUserMutation = useMutation<UserUpsertResult, Error, Partial<User>>({
    mutationFn: async (user) => {
      if (!customerId || !sessionId) {
        throw new Error('Missing customer ID or session ID')
      }

      const response = await axios.post(
        `/api/v1/admin/customers/${customerId}/users`,
        user,
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'x-session-id': sessionId,
          },
        }
      )

      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'customers', customerId, 'users'] })
    },
  })

  // Update User Mutation
  const updateUserMutation = useMutation<UserUpsertResult, Error, { userId: string; user: Partial<User> }>({
    mutationFn: async ({ userId, user }) => {
      if (!sessionId) {
        throw new Error('Missing session ID')
      }

      const response = await axios.put(
        `/api/v1/admin/users/${userId}`,
        user,
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'x-session-id': sessionId,
          },
        }
      )

      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'customers', customerId, 'users'] })
    },
  })

  // Remove User From Customer Mutation. Not the global user delete
  // (DELETE /admin/users/:id), which licensing carries out on every customer
  // the person belongs to (4 Nov 2026).
  const deleteUserMutation = useMutation<{ accountDeleted: boolean }, Error, string>({
    mutationFn: async (userId) => {
      if (!sessionId) {
        throw new Error('Missing session ID')
      }
      if (!customerId) {
        throw new Error('Missing customer')
      }

      const response = await axios.delete<{ success: boolean; account_deleted?: boolean }>(
        `/api/v1/admin/customers/${encodeURIComponent(customerId)}/users/${encodeURIComponent(userId)}`,
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'x-session-id': sessionId,
          },
        }
      )
      return { accountDeleted: !!response.data?.account_deleted }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'customers', customerId, 'users'] })
    },
  })

  // Reset Password Mutation
  const resetPasswordMutation = useMutation<UserUpsertResult, Error, { userId: string; password: string }>({
    mutationFn: async ({ userId, password }) => {
      if (!sessionId) {
        throw new Error('Missing session ID')
      }

      const response = await axios.put(
        `/api/v1/admin/users/${userId}/password`,
        { password },
        {
          params: { customer: customerId },
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'x-session-id': sessionId,
          },
        }
      )

      return response.data
    },
  })

  // The whole roles string (Manager only). The server tidies it and answers
  // 400 with a sentence on a bad one, 409 on a Manager's own Manager role.
  const setUserRolesStringMutation = useMutation<void, Error, { userId: string; roles: string }>({
    mutationFn: async ({ userId, roles }) => {
      if (!sessionId) {
        throw new Error('Missing session ID')
      }
      await axios.put(
        `/api/v1/admin/users/${userId}/roles-string`,
        { roles },
        {
          params: { customer: customerId },
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'x-session-id': sessionId,
          },
        }
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'customers', customerId, 'users'] })
    },
  })

  // Update Users Roles Mutation (Batch)
  const updateUsersRolesMutation = useMutation<void, Error, UserRoleUpdate[], { previousUsers?: User[] }>({
    mutationFn: async (updates) => {
      if (!sessionId) {
        throw new Error('Missing session ID')
      }

      await axios.put(
        `/api/v1/admin/users/roles`,
        updates,
        {
          params: { customer: customerId },
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'x-session-id': sessionId,
          },
        }
      )
    },
    onMutate: (updates) => {
      const usersKey = ['admin', 'customers', customerId, 'users']
      const previousUsers = queryClient.getQueryData<User[]>(usersKey)

      if (previousUsers) {
        const updatedUsers = previousUsers.map(user => {
          const userId = user.id || user.uid || ''
          const update = updates.find(u => u.user_id === userId)
          if (!update) {
            return user
          }

          const updatedRoles = applyRoleUpdate(user, update)
          return {
            ...user,
            roles: updatedRoles,
          }
        })

        queryClient.setQueryData(usersKey, updatedUsers)
      }

      return { previousUsers }
    },
    onError: (_error, _updates, context) => {
      const usersKey = ['admin', 'customers', customerId, 'users']
      if (context?.previousUsers) {
        queryClient.setQueryData(usersKey, context.previousUsers)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'customers', customerId, 'users'] })
    },
  })

  // Create Job Mutation
  const createJobMutation = useMutation<Job, Error, Partial<Job>>({
    mutationFn: async (job) => {
      if (!customerId || !sessionId) {
        throw new Error('Missing customer ID or session ID')
      }

      const response = await axios.post(
        `/api/v1/admin/customers/${customerId}/jobs`,
        job,
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'x-session-id': sessionId,
          },
        }
      )

      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'customers', customerId, 'jobs'] })
    },
  })

  // Update Job Mutation
  const updateJobMutation = useMutation<Job, Error, { jobId: string; job: Partial<Job> }>({
    mutationFn: async ({ jobId, job }) => {
      if (!sessionId) {
        throw new Error('Missing session ID')
      }

      const response = await axios.put(
        `/api/v1/admin/jobs/${jobId}`,
        job,
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'x-session-id': sessionId,
          },
        }
      )

      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'customers', customerId, 'jobs'] })
    },
  })

  // Delete Job Mutation
  const deleteJobMutation = useMutation<void, Error, string>({
    mutationFn: async (jobId) => {
      if (!sessionId) {
        throw new Error('Missing session ID')
      }

      await axios.delete(
        `/api/v1/admin/jobs/${jobId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'x-session-id': sessionId,
          },
        }
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'customers', customerId, 'jobs'] })
    },
  })

  // Calculate license limits from licenses data
  const platinumLimit = calculateActiveLicenses(licensesQuery.data?.licences, PLATINUM_ROLE)
  const rubyLimit = calculateActiveLicenses(licensesQuery.data?.licences, RUBY_ROLE)

  const parseRoles = (roles?: string | null): string[] => {
    if (!roles) return []
    return roles.split(',').map(r => r.trim()).filter(Boolean)
  }

  const applyRoleUpdate = (user: User, update: UserRoleUpdate): string => {
    const existingRoles = parseRoles(user.roles)
    const baseRoles = existingRoles.filter(
      role => role !== PLATINUM_ROLE && role !== RUBY_ROLE && role !== ADMIN_ROLE
    )

    if (update.platinum) {
      baseRoles.push(PLATINUM_ROLE)
    }
    if (update.ruby) {
      baseRoles.push(RUBY_ROLE)
    }
    if (update.admin) {
      baseRoles.push(ADMIN_ROLE)
    }

    return baseRoles.join(',')
  }

  return {
    // Users
    users: usersQuery.data || [],
    usersLoading: usersQuery.isLoading,
    usersError: usersQuery.error?.message || null,
    refetchUsers: usersQuery.refetch,

    // Jobs
    jobs: jobsQuery.data || [],
    jobsLoading: jobsQuery.isLoading,
    jobsError: jobsQuery.error?.message || null,
    refetchJobs: jobsQuery.refetch,

    // License Limits
    platinumLimit,
    rubyLimit,
    licensesLoading: licensesQuery.isLoading,
    licensesError: licensesQuery.error?.message || null,

    // User Mutations
    createUser: (user: Partial<User>) => createUserMutation.mutateAsync(user),
    updateUser: (userId: string, user: Partial<User>) => updateUserMutation.mutateAsync({ userId, user }),
    removeUserFromCustomer: (userId: string) => deleteUserMutation.mutateAsync(userId),
    resetPassword: (userId: string, password: string) => resetPasswordMutation.mutateAsync({ userId, password }),
    updateUsersRoles: (updates: UserRoleUpdate[]) => updateUsersRolesMutation.mutateAsync(updates),
    setUserRolesString: (userId: string, roles: string) => setUserRolesStringMutation.mutateAsync({ userId, roles }),

    // Job Mutations
    createJob: (job: Partial<Job>) => createJobMutation.mutateAsync(job),
    updateJob: (jobId: string, job: Partial<Job>) => updateJobMutation.mutateAsync({ jobId, job }),
    deleteJob: (jobId: string) => deleteJobMutation.mutateAsync(jobId),

    // Mutation states
    isCreating: createUserMutation.isPending,
    isUpdating: updateUserMutation.isPending,
    isDeleting: deleteUserMutation.isPending,
    isUpdatingRoles: updateUsersRolesMutation.isPending,
    isCreatingJob: createJobMutation.isPending,
    isUpdatingJob: updateJobMutation.isPending,
    isDeletingJob: deleteJobMutation.isPending,
    mutationError:
      createUserMutation.error?.message ||
      updateUserMutation.error?.message ||
      deleteUserMutation.error?.message ||
      resetPasswordMutation.error?.message ||
      updateUsersRolesMutation.error?.message ||
      createJobMutation.error?.message ||
      updateJobMutation.error?.message ||
      deleteJobMutation.error?.message ||
      null,
  }
}

/** What a member PUT writes: the team (null = No budget team) and AI level. */
export interface MemberUpdate {
  team_id: string | null
  ai_level: number
}

// The customer's budget teams (GET /teams), shared by useBudgetTeams and
// useTeams under one query key, so the two read one cache.
const teamsQueryOptions = (customer: string) => ({
  queryKey: ['admin', 'budget-teams', customer, 'teams'],
  enabled: Boolean(customer),
  queryFn: async (): Promise<Team[]> => {
    try {
      return (await axios.get<Team[]>(`${API_BASE_URL}/admin/customers/${encodeURIComponent(customer)}/teams`)).data ?? []
    } catch (error) {
      throw new Error(extractErrorMessage(error))
    }
  },
})

/**
 * The customer's budget teams alone, for a team picker (a pipeline's billing
 * team): useBudgetTeams without the members and people reads.
 */
export const useTeams = (customer: string) => {
  const query = useQuery<Team[], Error>(teamsQueryOptions(customer))
  return { teams: query.data ?? [], teamsLoading: query.isLoading, teamsError: query.error?.message ?? '' }
}

/**
 * Budget teams in User management (#1523). customer is the customer NAME —
 * the teams routes check membership by name, unlike the licensing-id user
 * routes above. Members are joined to users on email by the page. people is
 * everyone who reaches the customer, including those who reach it only
 * through one of its jobs.
 */
export const useBudgetTeams = (customer: string) => {
  const queryClient = useQueryClient()
  const teamsKey = teamsQueryOptions(customer).queryKey
  const membersKey = ['admin', 'budget-teams', customer, 'members']
  const peopleKey = ['admin', 'budget-teams', customer, 'people']
  const base = `${API_BASE_URL}/admin/customers/${encodeURIComponent(customer)}`

  const teamsQuery = useQuery<Team[], Error>(teamsQueryOptions(customer))

  const membersQuery = useQuery<CustomerMember[], Error>({
    queryKey: membersKey,
    enabled: Boolean(customer),
    queryFn: async () => {
      try {
        return (await axios.get<CustomerMember[]>(`${base}/members`)).data ?? []
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
  })

  const peopleQuery = useQuery<CustomerPerson[], Error>({
    queryKey: peopleKey,
    enabled: Boolean(customer),
    queryFn: async () => {
      try {
        return (await axios.get<CustomerPerson[]>(`${base}/people`)).data ?? []
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
  })

  // Create, rename, archive and unarchive (#1563), Manager only. A refusal
  // rejects with the server's message (a 409 says why). The AI budgets page
  // lists every team by name and hides an archived one without a budget, so
  // its query is refreshed too: without it a new team had no card until the
  // cached list went stale.
  const teamWrite = async (request: () => Promise<{ data: Team }>): Promise<Team> => {
    try {
      return (await request()).data
    } catch (error) {
      throw new Error(extractErrorMessage(error))
    }
  }
  const refreshTeams = () => Promise.all([
    queryClient.invalidateQueries({ queryKey: teamsKey }),
    queryClient.invalidateQueries({ queryKey: ['admin', 'budgets', customer] }),
  ])
  const createTeamMutation = useMutation<Team, Error, string>({
    mutationFn: async (name) => (await axios.post<Team>(`${base}/teams`, { name })).data,
    onSuccess: refreshTeams,
  })
  const teamPath = (id: string) => `${base}/teams/${encodeURIComponent(id)}`
  const renameTeamMutation = useMutation<Team, Error, { id: string; name: string }>({
    mutationFn: ({ id, name }) => teamWrite(() => axios.patch<Team>(teamPath(id), { name })),
    onSuccess: refreshTeams,
  })
  const archiveTeamMutation = useMutation<Team, Error, string>({
    mutationFn: (id) => teamWrite(() => axios.post<Team>(`${teamPath(id)}/archive`)),
    onSuccess: refreshTeams,
  })
  const unarchiveTeamMutation = useMutation<Team, Error, string>({
    mutationFn: (id) => teamWrite(() => axios.post<Team>(`${teamPath(id)}/unarchive`)),
    onSuccess: refreshTeams,
  })

  // Appoint and remove a team's managers (000146), Realm admin or Manager
  // only. The responses carry no budget, so the list is read again, never
  // patched from the answer.
  const managerPath = (teamId: string, email: string) => `${teamPath(teamId)}/managers/${encodeURIComponent(email.toLowerCase())}`
  const appointManagerMutation = useMutation<unknown, Error, { teamId: string; email: string }>({
    mutationFn: ({ teamId, email }) => teamWrite(() => axios.put<Team>(managerPath(teamId, email))),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: teamsKey }),
  })
  const removeManagerMutation = useMutation<unknown, Error, { teamId: string; email: string }>({
    mutationFn: ({ teamId, email }) => teamWrite(() => axios.delete<Team>(managerPath(teamId, email))),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: teamsKey }),
  })

  const putMember = async (email: string, update: MemberUpdate): Promise<CustomerMember> =>
    (await axios.put<CustomerMember>(`${base}/members/${encodeURIComponent(email.toLowerCase())}`, update)).data

  // One row changes: show it at once, put it back if the server refuses.
  const setMemberMutation = useMutation<CustomerMember, Error, { email: string; update: MemberUpdate }, { previous?: CustomerMember[] }>({
    mutationFn: ({ email, update }) => putMember(email, update),
    onMutate: ({ email, update }) => {
      const previous = queryClient.getQueryData<CustomerMember[]>(membersKey)
      const lower = email.toLowerCase()
      const rest = (previous ?? []).filter((m) => m.email !== lower)
      // The server keeps hidden across a write, so the guess does too.
      const hidden = (previous ?? []).some((m) => m.email === lower && m.hidden)
      queryClient.setQueryData<CustomerMember[]>(membersKey, [
        ...rest,
        { customer, email: lower, team_id: update.team_id, ai_level: update.ai_level, hidden, updated_by: '', updated_at: Date.now() },
      ])
      return { previous }
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(membersKey, context.previous)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: membersKey }),
  })

  // "Set for everyone in this team…": one PUT per member, so each row is
  // checked on its own (a Manager in the list is refused, the rest still land).
  const setTeamLevelMutation = useMutation<{ failed: string[] }, Error, { teamId: string; emails: string[]; level: number }>({
    mutationFn: async ({ teamId, emails, level }) => {
      const results = await Promise.allSettled(emails.map((email) => putMember(email, { team_id: teamId, ai_level: level })))
      return { failed: emails.filter((_e, i) => results[i].status === 'rejected') }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: membersKey }),
  })

  return {
    teams: teamsQuery.data ?? [],
    members: membersQuery.data ?? [],
    people: peopleQuery.data ?? [],
    teamsLoading: teamsQuery.isLoading || membersQuery.isLoading,
    teamsError: teamsQuery.error?.message || membersQuery.error?.message || null,
    peopleError: peopleQuery.error?.message || null,
    handleCreateTeam: (name: string) => createTeamMutation.mutateAsync(name),
    handleSetMember: (email: string, update: MemberUpdate) => setMemberMutation.mutateAsync({ email, update }),
    handleSetTeamLevel: (teamId: string, emails: string[], level: number) => setTeamLevelMutation.mutateAsync({ teamId, emails, level }),
    isCreatingTeam: createTeamMutation.isPending,
    handleRenameTeam: (id: string, name: string) => renameTeamMutation.mutateAsync({ id, name }),
    handleArchiveTeam: (id: string) => archiveTeamMutation.mutateAsync(id),
    handleUnarchiveTeam: (id: string) => unarchiveTeamMutation.mutateAsync(id),
    handleAppointManager: (teamId: string, email: string) => appointManagerMutation.mutateAsync({ teamId, email }),
    handleRemoveManager: (teamId: string, email: string) => removeManagerMutation.mutateAsync({ teamId, email }),
  }
}

/**
 * The Finance view (migration 000153) in User management: the people a
 * Manager granted the hours-saved figure and finance-only programme trackers.
 * Manager only on the server, so a caller who is not one passes enabled
 * false and nothing is fetched. viewers are lower-cased emails. A switch
 * shows its new state at once and goes back if the server refuses; the
 * refusal rejects with the server's message.
 */
export const useFinanceViewers = (customer: string, enabled: boolean) => {
  const queryClient = useQueryClient()
  const key = ['admin', 'budget-teams', customer, 'finance-viewers']
  const base = `${API_BASE_URL}/admin/customers/${encodeURIComponent(customer)}/finance-viewers`

  const query = useQuery<FinanceViewer[], Error>({
    queryKey: key,
    enabled: Boolean(customer) && enabled,
    queryFn: async () => {
      try {
        return (await axios.get<FinanceViewer[]>(base)).data ?? []
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
  })

  const setMutation = useMutation<unknown, Error, { email: string; on: boolean }, { previous?: FinanceViewer[] }>({
    mutationFn: async ({ email, on }) => {
      const url = `${base}/${encodeURIComponent(email.toLowerCase())}`
      try {
        return on ? (await axios.put<FinanceViewer>(url)).data : (await axios.delete(url)).data
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
    onMutate: ({ email, on }) => {
      const previous = queryClient.getQueryData<FinanceViewer[]>(key)
      const lower = email.toLowerCase()
      const rest = (previous ?? []).filter((v) => v.email !== lower)
      queryClient.setQueryData<FinanceViewer[]>(key, on ? [...rest, { email: lower, granted_by: '', granted_at: Date.now() }] : rest)
      return { previous }
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  })

  return {
    viewers: (query.data ?? []).map((v) => v.email),
    viewersError: query.error?.message ?? null,
    handleSetFinanceView: async (email: string, on: boolean): Promise<void> => {
      await setMutation.mutateAsync({ email, on })
    },
  }
}

/**
 * The customer's email domains (migration 000156) in User management. When
 * the list is set, a non-Manager sees a colleague's work only when the
 * owner's email domain is on it. Manager only on the server, so a caller who
 * is not one passes enabled false and nothing is fetched. domains are
 * lower-cased and sorted. handleSetDomains replaces the whole list ([]
 * clears it) and rejects with the server's message.
 */
export const useEmailDomains = (customer: string, enabled: boolean) => {
  const queryClient = useQueryClient()
  const key = ['admin', 'email-domains', customer]
  const url = `${API_BASE_URL}/admin/customers/${encodeURIComponent(customer)}/email-domains`

  const query = useQuery<CustomerEmailDomainsResponse, Error>({
    queryKey: key,
    enabled: Boolean(customer) && enabled,
    queryFn: async () => {
      try {
        return (await axios.get<CustomerEmailDomainsResponse>(url)).data
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
  })

  const setMutation = useMutation<CustomerEmailDomainsResponse, Error, string[]>({
    mutationFn: async (domains) => {
      try {
        return (await axios.put<CustomerEmailDomainsResponse>(url, { domains })).data
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
    onSuccess: (data) => queryClient.setQueryData(key, data),
  })

  return {
    domains: (query.data?.domains ?? []).map((d) => d.domain),
    loaded: query.isSuccess,
    domainsError: query.error?.message ?? null,
    isSavingDomains: setMutation.isPending,
    handleSetDomains: async (domains: string[]): Promise<void> => {
      await setMutation.mutateAsync(domains)
    },
  }
}
